import { Component, ViewEncapsulation, TemplateRef, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store, select } from '@ngrx/store';
import { map, delayWhen, pairwise, startWith } from 'rxjs/operators';
import { timer } from 'rxjs';
import { selectZefErrorByErrorType } from '../../errors.selector';
import { zefRemoveError } from '../../errors.action';
import { ZefError } from '../../errors.model';
import { ZefErrorTemplateService } from '../../error-template.service';

@Component({
  selector: 'zef-dialog-error',
  templateUrl: './dialog-error.container.html',
  styleUrls: [ './dialog-error.container.scss' ],
  encapsulation: ViewEncapsulation.None
})
export class DialogErrorContainer implements OnInit {
  tpl: TemplateRef<any>;
  dialogErrors$ = this._store.pipe(
    select(selectZefErrorByErrorType('dialog'))
  );
  dialogErrorsDelayed$ = this.dialogErrors$.pipe(
    startWith([]),
    pairwise(),
    delayWhen(([ prev, curr ]) => timer((prev?.length ?? 0) > 0 && (curr?.length ?? 0) === 0 ? 400 : 0)),
    map(([ _, curr ]) => curr ?? [])
  );
  isOpen$ = this.dialogErrors$.pipe(
    map((v) => !!v?.length)
  );

  // read synchronously by onCloseTrigger() - the errors being shown right now
  #dialogErrors = toSignal(this.dialogErrors$, { initialValue: [] as ZefError[] });

  constructor(
    private _store: Store<any>,
    private _errorTemplate: ZefErrorTemplateService
  ) { }

  ngOnInit() {
    setTimeout(() => {
      this.tpl = this._errorTemplate.getTemplate('dialog');
    });
  }

  /**
   * Dismissing the dialog by any route other than an in-content action - Escape,
   * above all - has to clear the errors it was showing.
   *
   * `open` is bound to "are there any dialog errors", so leaving them in the store
   * wedges the dialog shut: the binding stays `true`, Angular skips the setter
   * because the value did not change, and no later error can ever reopen it. The
   * errors reducer appends rather than dedupes, so every subsequent failure piles up
   * unseen while the requests behind them keep failing (a sudo re-auth prompt
   * dismissed this way leaves reveals returning 403 with no way back).
   *
   * Removing by key only - never by id - because the reducer's id branch keeps just
   * the matching key and would drop unrelated errors.
   */
  onCloseTrigger() {
    const keys = new Set((this.#dialogErrors() ?? []).map((error) => error.key));
    keys.forEach((key) => this._store.dispatch(zefRemoveError(key)));
  }
}
